import React from 'react';
import { useSearchParams } from 'react-router-dom';
import './insuranceResult.scss';

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────── */
const ADDONS = [
  { key: 'zero_dep',            label: 'Zero Depreciation',  desc: 'Full part cost — no deduction',   badge: null },
  { key: 'return_to_invoice',   label: 'Return to Invoice',  desc: 'Get original invoice value back', badge: 'Popular' },
  { key: 'engine_protection',   label: 'Engine Protection',  desc: 'Engine & gearbox damage cover',   badge: null },
  { key: 'key_lock_protection', label: 'Key & Lock',         desc: 'Lost key replacement cover',      badge: null },
  { key: 'tyre_protection',     label: 'Tyre Protection',    desc: 'Tyre & tube damage cover',        badge: null },
];

const PLANS = [
  { key: 'standard',         label: '1 Year', rec: false },
  { key: 'long_term_3_year', label: '3 Year', rec: true  },
  { key: 'long_term_5_year', label: '5 Year', rec: false },
];

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */
const inr  = n => '₹' + Math.round(n).toLocaleString('en-IN');
const inrL = n => {
  const r = Math.round(n);
  return r >= 100000 ? '₹' + (r / 100000).toFixed(2) + 'L' : '₹' + r.toLocaleString('en-IN');
};
const inrFull = n => {
  const parts = n.toFixed(2).split('.');
  const wholePart = parseInt(parts[0]).toLocaleString('en-IN');
  // Remove .00 if decimal is 00, otherwise keep the decimals
  if (parts[1] === '00') {
    return '₹' + wholePart;
  }
  return '₹' + wholePart + '.' + parts[1];
};
const idvFor  = (price, age) => price * (1 - ({ 1:.15, 2:.20, 3:.30, 4:.40 }[age] ?? .50));
const planYrs = key => key === 'long_term_3_year' ? 3 : key === 'long_term_5_year' ? 5 : 1;

/* ─────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────── */
const InsuranceResult = () => {
  const [searchParams] = useSearchParams();

  const [premiumData,    setPremiumData]    = React.useState(null);
  const [loading,        setLoading]        = React.useState(true);
  const [error,          setError]          = React.useState(null);
  const [activePlan,     setActivePlan]     = React.useState('standard');
  const [selectedAddons, setSelectedAddons] = React.useState(new Set(ADDONS.map(a => a.key)));

  /* fetch */
  React.useEffect(() => {
    const condition = searchParams.get('condition');
    const make      = searchParams.get('make');
    const model     = searchParams.get('model');
    const year      = searchParams.get('year');
    const trim      = searchParams.get('trim');
    const city      = searchParams.get('city');

    if (!condition || !make || !model || !year || !trim || !city) {
      setError('Missing required parameters in URL.');
      setLoading(false);
      return;
    }

    fetch('/calculateinsurancepremium', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ insurance_condition: condition, make, model, year, trim, city }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.code === 'success' && data.data) setPremiumData(data.data);
        else setError('Could not calculate premium for this vehicle.');
        setLoading(false);
      })
      .catch(() => { setError('Network error — please try again.'); setLoading(false); });
  }, [searchParams]);

  /* addon toggle */
  const toggleAddon = key => setSelectedAddons(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  /* derived totals */
  const totals = React.useMemo(() => {
    if (!premiumData) return null;
    const plan      = premiumData[activePlan];
    const addonsSum = ADDONS.filter(a => selectedAddons.has(a.key)).reduce((s, a) => s + (plan[a.key] ?? 0), 0);
    const preTax    = plan.od_premium_after_ncb + plan.tp_premium + addonsSum;
    const gst       = preTax * 0.18;
    return { addonsSum, preTax, gst, total: preTax + gst };
  }, [premiumData, activePlan, selectedAddons]);

  /* states */
  if (loading) return (
    <div className="ir-wrap"><div className="ir-state"><div className="ir-spinner" /><span>Calculating your premium…</span></div></div>
  );
  if (error) return (
    <div className="ir-wrap"><div className="ir-state"><span style={{ fontSize: 30 }}>⚠️</span><span>{error}</span></div></div>
  );
  if (!premiumData) return null;

  const plan      = premiumData[activePlan];
  const yrs       = planYrs(activePlan);
  const ncbSaved  = plan.od_premium - plan.od_premium_after_ncb;
  const idv       = idvFor(premiumData.obv_price, premiumData.vehicle_age_years);
  const planLabel = PLANS.find(p => p.key === activePlan)?.label ?? '';
  const saving    = activePlan !== 'standard' ? (premiumData.standard.total_premium * yrs) - plan.total_premium : 0;

  // show a small uncertainty range of ±3% for displayed premiums
  const preLower = totals.preTax * 0.97;
  const preUpper = totals.preTax * 1.03;
  const totalLower = totals.total * 0.97;
  const totalUpper = totals.total * 1.03;

  const yearRows = activePlan !== 'standard'
    ? Array.from({ length: yrs }, (_, i) => {
        const od  = plan.od_premium_after_ncb / yrs;
        const tp  = plan.tp_premium / yrs;
        const add = ADDONS.filter(a => selectedAddons.has(a.key)).reduce((s, a) => s + (plan[a.key] ?? 0) / yrs, 0);
        return { yr: i + 1, od, tp, add, total: od + tp + add };
      })
    : [];

  return (
    <div className="ir-wrap">

      {/* HERO + PREMIUM (COMBINED) */}
      <div className="ir-hero-premium ir-a">
        <div className="ir-hero-left">
          <img className="ir-hero-img" src={premiumData.image} alt={premiumData.product_name} />
          <div className="ir-hero-info">
            <div className="ir-hero-name">{premiumData.product_name}</div>
            <div className="ir-hero-pills">
              <span className="ir-pill ir-pill-city">
                <svg className="ir-pill-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#fc712c"/>
                  <circle cx="12" cy="9" r="2.5" fill="#fff"/>
                </svg>
                <span className="ir-pill-text">{searchParams.get('city')}</span>
              </span>
            </div>
          </div>
        </div>
        <div className="ir-hero-right">
          <div className="ir-premium-lbl">Estimated Premium · {planLabel}</div>
          <div className="ir-premium-amount">{inr(preLower)} - {inr(preUpper)}</div>
          <div className="ir-premium-note">
            Pre-GST &nbsp;·&nbsp; Total payable incl. 18% GST:&nbsp;
            <strong style={{ color: '#435c7f' }}>{inr(totalLower)} - {inr(totalUpper)}</strong>
          </div>
          {activePlan !== 'standard' && saving > 0 && (
            <div className="ir-saving-chip">
              ✓ Saves approx. {inr(saving)} vs {yrs} × 1-year policies
            </div>
          )}
        </div>
      </div>

     
      {/* PLAN TABS */}
      <div className="ir-tabs ir-a ir-a2">
        {PLANS.map(opt => (
          <button
            key={opt.key}
            className={`ir-tab ${activePlan === opt.key ? 'ir-tab-active' : ''}`}
            onClick={() => setActivePlan(opt.key)}
          >
            {opt.label}
            {opt.rec && <span className="ir-tab-rec">Best value</span>}
          </button>
        ))}
      </div>
 {/* IDV & OBV COMPARISON */}
      <div className="ir-idv ir-a ir-a1">
        <div className="ir-idv-comparison">
          {/* OBV VALUE */}
          <div className="ir-value-card ir-idv-card">
            <div className="ir-value-label">OBV Value</div>
            <div className="ir-value-amount">{inrFull(premiumData.obv_price)}</div>
            <div className="ir-value-note">Market valuation based on OBV algorithm</div>
          </div>

          {/* IDV VALUE */}
          <div className="ir-value-card ir-obv-card">
            <div className="ir-value-label">IDV (Insurance Declared Value)</div>
            {premiumData.vehicle_age_years > 5 ? (
              <div style={{ padding: '12px 0' }}>
                <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.6', textAlign: 'center' }}>
                  IDV in insurance is calculated by <strong>mutual agreement</strong> between the insurer and the policyholder based on the <strong>vehicle's condition</strong>.
                </div>
              </div>
            ) : (
              <>
                <div className="ir-value-amount">{inr(premiumData.idv || idv)}</div>
                <div className="ir-value-note">Maximum insurance payout amount</div>
              </>
            )}
          </div>
        </div>
      </div>

    

      {/* NCB */}
      {plan.ncb_percentage > 0 && (
        <div className="ir-ncb ir-a ir-a2">
          <div className="ir-ncb-icon">🎁</div>
          <div className="ir-ncb-text">
            <strong>No Claim Bonus {plan.ncb_percentage}% applied</strong>
            {' '}— saving you <strong>{inr(ncbSaved)}</strong> on your OD premium.
            Stay claim-free to reach 50%.
          </div>
        </div>
      )}

      {/* BREAKDOWN */}
      <div className="ir-section ir-a ir-a3">Premium Breakdown</div>
      <div className="ir-breakdown-grid ir-a ir-a3">

        <div className="ir-card">
          <div className="ir-card-head">🚗 Own Damage (OD)</div>
          <div className="ir-row">
            <span className="ir-row-lbl">Base OD Premium</span>
            <span className="ir-row-val">{inr(plan.od_premium)}</span>
          </div>
          <div className="ir-row">
            <span className="ir-row-lbl">NCB Discount ({plan.ncb_percentage}%)</span>
            <span className="ir-row-val-green">− {inr(ncbSaved)}</span>
          </div>
          <div className="ir-row-total">
            <span className="ir-row-total-lbl">After NCB</span>
            <span className="ir-row-val-orange">{inr(plan.od_premium_after_ncb)}</span>
          </div>
        </div>

        <div className="ir-card">
          <div className="ir-card-head">⚖️ Third Party (TP)</div>
          <div className="ir-row">
            <span className="ir-row-lbl">TP Premium</span>
            <span className="ir-row-val">{inr(plan.tp_premium)}</span>
          </div>
          <div className="ir-row">
            <span className="ir-row-lbl">Regulated by IRDAI</span>
            <span className="ir-row-val-hint">Fixed by law</span>
          </div>
          <div className="ir-row-total">
            <span className="ir-row-total-lbl">Mandatory Cover</span>
            <span className="ir-row-val-orange">{inr(plan.tp_premium)}</span>
          </div>
        </div>

      </div>

      
   {/* ADD-ONS */}
      <div className="ir-section ir-a ir-a4">Add-On Covers</div>
      <div className="ir-addon-grid ir-a ir-a4">
        {ADDONS.map(addon => {
          const on = selectedAddons.has(addon.key);
          return (
            <div
              key={addon.key}
              className={`ir-addon ${on ? 'ir-addon-on' : ''}`}
              onClick={() => toggleAddon(addon.key)}
            >
              <div className="ir-addon-check">
                {on && (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2.2"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div className="ir-addon-info">
                <div className="ir-addon-name">{addon.label}</div>
                <div className="ir-addon-desc">{addon.desc}</div>
              </div>
              <div className="ir-addon-right">
                <span className="ir-addon-price">{inr(plan[addon.key] ?? 0)}</span>
                {addon.badge && <span className="ir-addon-badge">{addon.badge}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* FINAL SUMMARY */}
      <div className="ir-summary ir-a ir-a5">
        <div className="ir-section">Final Premium Summary</div>
        <div className="ir-card">
          <div className="ir-row">
            <span className="ir-row-lbl">OD Premium (after NCB)</span>
            <span className="ir-row-val">{inr(plan.od_premium_after_ncb)}</span>
          </div>
          <div className="ir-row">
            <span className="ir-row-lbl">Third Party Premium</span>
            <span className="ir-row-val">{inr(plan.tp_premium)}</span>
          </div>
          <div className="ir-row">
            <span className="ir-row-lbl">Add-Ons ({selectedAddons.size} selected)</span>
            <span className="ir-row-val">{inr(totals.addonsSum)}</span>
          </div>
          <div className="ir-row">
            <span className="ir-row-lbl">GST (18%)</span>
            <span className="ir-row-val">{inr(totals.gst)}</span>
          </div>
          <div className="ir-row-total">
            <span className="ir-row-total-lbl" style={{ fontSize: 15 }}>Total Payable</span>
            <span className="ir-row-val-orange" style={{ fontSize: 20 }}>{inr(totals.total)}</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default InsuranceResult;